import React, { useState, useEffect } from 'react';

/**
 * KinAdminContributions
 *
 * KC-only panel showing pending contributions for a Kin.
 *
 * Actions per pending pledge:
 *   - Copy the member's deposit reference
 *   - Remind the member by email
 *   - Record an off-system receipt (cash or third party)
 *
 * The pledge remains pending in the ledger until the deposit reaches
 * the VA. Off-system receipt marks that the member has paid the KC;
 * the KC still needs to deposit the funds using the member's reference.
 */
export default function KinAdminContributions({ kinId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState([]);
  const [flash, setFlash] = useState(null);
  const [modal, setModal] = useState(null); // { id, method, note }

  useEffect(() => {
    if (kinId) fetchPending();
  }, [kinId]);

  const fetchPending = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/kins/${kinId}/contributions/pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      setPending(result.pending || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyText = (text) => {
    try {
      navigator.clipboard.writeText(text);
      setFlash('Copied to clipboard');
    } catch (err) {
      console.error(err);
    }
  };

  const remindMember = async (id) => {
    try {
      const res = await fetch(
        `/api/kins/${kinId}/contributions/${id}/remind`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to send reminder');
      }
      const result = await res.json();
      setFlash(`Reminder sent to ${result.sentTo}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const openPledgeReceived = (id) => {
    setModal({ id, method: 'cash', note: '' });
  };

  const submitPledgeReceived = async () => {
    if (!modal) return;
    try {
      const res = await fetch(
        `/api/kins/${kinId}/contributions/${modal.id}/pledge-received`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ method: modal.method, note: modal.note }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to record');
      }
      setFlash('Pledge marked as received');
      setModal(null);
      await fetchPending();
    } catch (err) {
      setError(err.message);
    }
  };

  const fmt = (n) => '$' + (Number(n) || 0).toFixed(2);

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div>
      {flash && (
        <div className="mb-3 bg-green-50 border border-green-200 text-green-800 rounded-lg p-2 text-sm">
          ✅ {flash}
        </div>
      )}

      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">
          {error}
        </div>
      )}

      {pending.length === 0 ? (
        <p className="text-sm text-gray-500">No pending contributions.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {pending.map((p) => (
            <div
              key={p.id}
              className="py-3 flex flex-wrap items-center gap-3"
            >
              <div className="flex-1 min-w-[200px]">
                <div className="font-medium">
                  {p.memberName}
                  {p.isSecurityDeposit && (
                    <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                      Security deposit
                    </span>
                  )}
                </div>
                {p.memberEmail && (
                  <div className="text-xs text-gray-500">{p.memberEmail}</div>
                )}
                {p.note && (
                  <div className="text-xs text-gray-500 italic mt-0.5">
                    "{p.note}"
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Pledged{' '}
                  {new Date(p.createdAt).toLocaleDateString('en-AU', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </div>
                {p.offsystemReceipt && (
                  <div className="text-xs text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded inline-block">
                    ✓ Received ({p.offsystemReceipt.method}) — still to deposit to VA
                  </div>
                )}
              </div>

              <div className="text-right">
                <div className="text-lg font-bold text-amber-700">
                  {fmt(p.amount)}
                </div>
                {p.pledgeReference ? (
                  <button
                    onClick={() => copyText(p.pledgeReference)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-mono"
                    title="Click to copy"
                  >
                    {p.pledgeReference}
                  </button>
                ) : (
                  <div className="text-xs text-gray-400">no reference</div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => remindMember(p.id)}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                  title="Send a reminder with the deposit reference"
                >
                  Remind
                </button>
                {!p.offsystemReceipt && (
                  <button
                    onClick={() => openPledgeReceived(p.id)}
                    className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700"
                    title="Record that you've received this payment off-system"
                  >
                    Pledge received
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-3 border-t pt-3">
        💡 When a member deposits to the Kin account with their reference, the
        deposit is matched automatically. Use "Pledge received" for cash or
        third-party payments — you still need to deposit the funds to the VA
        using the member's reference.
      </p>

      {modal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-3">Pledge received</h3>
            <p className="text-sm text-gray-600 mb-4">
              Record that the member has paid you directly. You still need to
              deposit the funds to the Kin account using their reference.
            </p>
            <label className="block text-sm font-medium mb-1">Method</label>
            <select
              value={modal.method}
              onChange={(e) => setModal({ ...modal, method: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3"
            >
              <option value="cash">Cash</option>
              <option value="third_party">Third-party transfer</option>
              <option value="other">Other</option>
            </select>
            <label className="block text-sm font-medium mb-1">Note (optional)</label>
            <textarea
              value={modal.note}
              onChange={(e) => setModal({ ...modal, note: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-4"
              rows="3"
              maxLength={500}
              placeholder="What happened?"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setModal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={submitPledgeReceived}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
              >
                Record received
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
